import TextareaAutosize from 'react-textarea-autosize';
import VoiceEqualizer from './VoiceEqualizer';
import { Microphone } from '@phosphor-icons/react';
import { RootState, useAppSelector } from '../../../../store';

interface MessageInputProps {
    textInputRef: any;
    newMessage: string;
    handleInputChange: (e: any) => void;
    handleKeyDown: (e: any) => void;
    handlePaste: (e: any) => void;
    numOfLines: number;
    i18n: any;
    isPreviewPrompt?: boolean;
    showPiiText?: boolean;
    onTextareaClick?: () => void;
    isRecording?: boolean;
    volume?: number;
    onStartRecording?: () => void;
    isSending?: boolean;
}

export default function MessageInput({
    textInputRef,
    newMessage,
    handleInputChange,
    handleKeyDown,
    handlePaste,
    numOfLines,
    i18n,
    isPreviewPrompt = false,
    showPiiText = false,
    onTextareaClick,
    isRecording = false,
    volume = 0,
    onStartRecording,
    isSending = false,
}: MessageInputProps) {
    const settings = useAppSelector((state: RootState) => state.settings);
    const hasTranscribeModel = settings?.transcribeModel && settings.transcribeModel.trim() !== '';
    const getPiiTypeColor = (type: string): string => {
        switch (type?.toUpperCase()) {
            case 'CREDIT_CARD':
                return 'bg-blue-400';
            case 'IBAN_CODE':
                return 'bg-green-400';
            case 'CRYPTO':
                return 'bg-yellow-400';
            case 'PHONE_NUMBER':
                return 'bg-purple-400';
            case 'EMAIL_ADDRESS':
                return 'bg-red-400';
            case 'PERSON':
                return 'bg-orange-400';
            case 'IP_ADDRESS':
                return 'bg-pink-400';
            case 'NRP':
                return 'bg-teal-400';
            case 'LOCATION':
                return 'bg-indigo-400';
            case 'REGEX':
                return 'bg-slate-400';
            default:
                return 'bg-gray-400';
        }
    };

    const renderPiiText = (text: string) => {
        const parts = text.split(/(\〖[^〗]+〗)/);
        return parts.map((part, index) => {
            if (part.startsWith('〖') && part.endsWith('〗')) {
                const [value, type] = part.slice(1, -1).split('⟆');
                const bgColorClass = getPiiTypeColor(type);
                return (
                    <span key={index} className={`${bgColorClass} text-gray-900 rounded-sm px-0.5 mx-[0.5px]`}>
                        {value} &laquo;{type}&raquo;
                    </span>
                );
            }
            return (
                <span key={index} className="whitespace-pre-wrap">
                    {part}
                </span>
            );
        });
    };

    return (
        <div className="relative w-full" onClick={onTextareaClick}>
            {showPiiText && (
                <div
                    className="absolute left-0 top-0 w-full max-h-full overflow-y-auto sm:pl-6 sm:pr-6 py-4 pl-4 pr-4 select-none"
                    style={{ fontFamily: 'inherit', width: '100%' }}
                >
                    {renderPiiText(newMessage)}
                </div>
            )}

            {/* Mobile microphone icon on the right side of input */}
            {!isRecording && !isSending && newMessage.length === 0 && onStartRecording && hasTranscribeModel && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onStartRecording();
                    }}
                    className="sm:hidden absolute right-4 top-1/2 -translate-y-1/2 z-10 
                             text-chatInput-iconColor hover:text-chatInput-iconColor-hover 
                             transition-all duration-200 ease-out p-2 rounded-full 
                             hover:bg-chatInput-iconBackground active:scale-95
                             animate-in fade-in slide-in-from-right-2 duration-300"
                    aria-label={i18n.translate({ id: 'startRecording', sourceName: 'Chat' })}
                    title={i18n.translate({ id: 'transcribeAudio', sourceName: 'Chat' })}
                    type="button"
                >
                    <Microphone size={22} weight="regular" />
                </button>
            )}

            {isRecording ? (
                <div
                    className={`${
                        isPreviewPrompt ? 'bg-input-backgroundColor' : 'bg-chatInput-background'
                    } text-chatInput-textColor sm:pl-6 sm:pr-6 py-4 pl-4 pr-4 m-auto flex-1
                border border-chatInput-borderColor
                sm:border-none
                text-md
                sm:focus:border-transparent
                focus:border-chatInput-borderColor
                grow
                sm:w-full ${numOfLines > 2 ? 'rounded-lg sm:rounded-[1rem]' : 'rounded-lg sm:rounded-[2rem]'}`}
                >
                    <VoiceEqualizer volume={volume} isRecording={isRecording} />
                </div>
            ) : (
                <TextareaAutosize
                    ref={textInputRef}
                    minRows={1}
                    maxRows={20}
                    autoFocus={!isPreviewPrompt && !showPiiText}
                    value={newMessage}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    readOnly={showPiiText}
                    placeholder={i18n.translate({ id: 'enterMessage', sourceName: 'Chat' })}
                    style={{ fontFamily: 'inherit', width: '100%' }}
                    className={`${
                        isPreviewPrompt ? 'bg-input-backgroundColor' : 'bg-chatInput-background'
                    }  text-chatInput-textColor ${
                        showPiiText ? 'text-opacity-0' : 'text-opacity-100'
                    } placeholder:text-chatInput-playceHolderColor sm:pl-6 sm:pr-6 py-4 pl-4 ${
                        newMessage.length === 0 ? 'pr-12' : 'pr-4'
                    } m-auto flex-1
                    border border-chatInput-borderColor
                    sm:border-none
                    text-md
                    resize-none
                    sm:focus:border-transparent
                    focus:border-chatInput-borderColor
                    grow cursor-text focus-visible:outline-none
                    !outline-none
                    sm:w-full
                    transition-[width,height,border-radius,background-color,border,padding] duration-[300ms] ${
                        numOfLines > 2 ? 'rounded-lg sm:rounded-[1rem]' : 'rounded-lg sm:rounded-[2rem]'
                    }`}
                />
            )}
        </div>
    );
}
